import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Train } from '@mui/icons-material';

import { CONFIG } from '@frontend/config';
import { committedParty } from '../committedBookingContext';
import { PassPassengerPayload, PassesSearchPayload, useSearchPasses } from '../queries';
import {
  cleanParams,
  enumParam,
  isIncompleteSearch,
  numParam,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  EMPTY_PASS_FILTERS,
  PASSES_BOOKING_BLOCKED,
  PassFilters,
  PassOffer,
  filterPasses,
  groupPasses,
  isContinuous,
  productTitle,
} from '../passes';
import { useAudience } from '../audience';
import { CountrySelect } from './CountrySelect';
import { describePasses } from '../describeSearch';
import { SearchBar, SearchField } from './SearchBar';
import { RecentSearches } from './RecentSearches';
import { BookingModal } from './BookingModal';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import { releaseNumberInputOnWheel } from '../numberInput';
import {
  useSearchResultAuthority,
  useSearchSelectionBlockedReason,
} from '../useSearchResultAuthority';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { ResultFiltersBar } from './ResultFiltersBar';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import { FareRow } from './FareRow';
import { PriceBlock } from './PriceBlock';
import { MetaLine } from './MetaLine';
import { ResultMedia } from './ResultMedia';
import { StatusFlag } from './StatusFlag';
import { RuteroResultCard } from './RuteroResultCard';

const RESIDENCE_KEY = 'travelServices.passes.residence';
const TRAVEL_DAY_FILTERS = [4, 5, 7, 10, 15];

const EMPTY_PASSENGER: PassPassengerPayload = {
  type: 'adult',
  age: 30,
  nationality: '',
  countryOfResidence: '',
};

/**
 * Buscador de pases de tren. No hay origen ni destino: un pase habilita a
 * viajar por una RED durante una vigencia, así que lo único que define la
 * oferta son los pasajeros.
 *
 * Clave del vertical: la familia del producto (Interrail para residentes
 * europeos, Eurail para el resto) la decide el PROVEEDOR según la residencia
 * — no se le pregunta al asesor, porque elegir la equivocada devuelve cero
 * resultados sin explicación. Se busca sin filtro de producto (~300 bundles) y
 * el refinado es client-side, agrupando por producto.
 */

/**
 * Los pasajeros en la URL: "tipo:edad:nacionalidad" por pasajero. Es legible y
 * corto, que es lo que se busca en un link compartible.
 */
function passengersToParam(
  list: PassPassengerPayload[],
  residence: string,
): string {
  return list
    .map((pax) => `${pax.type}:${pax.age}:${pax.nationality || residence}`)
    .join(',');
}

function passengersFromParam(
  raw: string | undefined,
  residence: string,
): PassPassengerPayload[] {
  const parsed = (raw ?? '')
    .split(',')
    .map((entry) => entry.split(':'))
    .filter(([type, age]) => type && age)
    .map(([type, age, nationality]) => ({
      type: (['adult', 'youth', 'senior'].includes(type) ? type : 'adult') as
        PassPassengerPayload['type'],
      age: Math.max(0, Math.min(120, Number(age) || 30)),
      nationality: nationality ?? residence,
      countryOfResidence: residence,
    }));
  return parsed.length ? parsed : [{ ...EMPTY_PASSENGER }];
}

function payloadFromParams(
  params: Record<string, string>,
): PassesSearchPayload | null {
  const residence = params.residencia;
  if (params.buscar !== '1' || !residence) return null;
  const passengers = passengersFromParam(params.pax, residence);
  if (!passengers.every((pax) => pax.nationality)) return null;
  return { passengers };
}

export function PassesSearchForm() {
  const { t, i18n } = useTranslation();
  const { isAgent } = useAudience();
  const url = useSearchUrlState({ vertical: 'passes' });
  const searchPayload = useMemo(
    () => payloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchPasses(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('passes', CONFIG.AGENCY_ID, searchPayload)
    : undefined;

  // La URL le gana al localStorage: un link compartido se reproduce igual.
  const [residence, setResidence] = useState(
    () => url.params.residencia ?? localStorage.getItem(RESIDENCE_KEY) ?? '',
  );
  const [passengers, setPassengers] = useState<PassPassengerPayload[]>(() =>
    passengersFromParam(url.params.pax, url.params.residencia ?? ''));
  const [filters, setFilters] = useState<PassFilters>(() => ({
    ...EMPTY_PASS_FILTERS,
    travelClass: enumParam(url.params, 'fClase', ['', '1stclass', '2ndclass'] as const, ''),
    minTravelDays: numParam(url.params, 'fDias', 0),
    globalOnly: url.params.fGlobal === '1',
    query: url.params.fBusca ?? '',
  }));
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<PassOffer> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;
  const [expanded, setExpanded] = useState<string | null>(null);

  const { language } = i18n;

  const canSearch = Boolean(
    residence && passengers.length > 0 && passengers.every((pax) => pax.nationality),
  );

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    // La residencia es del grupo: define la familia del producto.
    residencia: residence,
    pax: passengersToParam(passengers, residence),
  };
  const draftPayload = payloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);
  const selectionBlockedReason = useSearchSelectionBlockedReason(resultAuthority);

  const setPax = (index: number, partial: Partial<PassPassengerPayload>) =>
    setPassengers((prev) =>
      prev.map((pax, i) => (i === index ? { ...pax, ...partial } : pax)));

  /**
   * Vacía la búsqueda: pasajeros, filtros, URL y memoria de navegación. La
   * residencia queda: es una preferencia del asesor, no parte de la búsqueda.
   */
  const handleClear = () => {
    setPassengers(passengersFromParam(undefined, residence));
    setFilters(EMPTY_PASS_FILTERS);
    url.reset();
  };

  const handleSearch = () => {
    if (!canSearch) return;
    localStorage.setItem(RESIDENCE_KEY, residence);
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('passes', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  useEffect(() => {
    url.update({
      fClase: filters.travelClass || undefined,
      fDias: filters.minTravelDays ? String(filters.minTravelDays) : undefined,
      fGlobal: filters.globalOnly ? '1' : undefined,
      fBusca: filters.query || undefined,
    });
  }, [filters, url]);

  /** Cuántos filtros están puestos: lo muestra el badge de la barra en mobile. */
  const activeFilterCount =
    (filters.query.trim() ? 1 : 0) +
    (filters.travelClass ? 1 : 0) +
    (filters.minTravelDays > 0 ? 1 : 0) +
    (filters.globalOnly ? 1 : 0);

  const groups = useMemo(() => {
    const offers = (search.data?.results ?? []) as PassOffer[];
    return groupPasses(filterPasses(offers, filters, language));
  }, [search.data, filters, language]);

  const totalOffers = search.data?.results.length ?? 0;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Un pase no tiene origen ni destino: lo único que decide el producto
          es la residencia. Va en la misma barra que los otros verticales, y
          los pasajeros (que son una lista, no un campo) quedan abajo. */}
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <Tooltip title={t('travelServices.passes.residenceHint')}>
          <Box sx={{ flexGrow: { md: 1 }, minWidth: 0 }}>
            <SearchField label={t('travelServices.passes.residence')} grow width={280}>
              <CountrySelect
                fullWidth
                required
                label={t('travelServices.passes.residence')}
                value={residence}
                onChange={(code) => setResidence(code ?? '')}
              />
            </SearchField>
          </Box>
        </Tooltip>
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="passes"
          describe={(params) => describePasses(params, t, i18n.language)}
        />
      )}

      <Box
        px={{ xs: 1.5, md: 2 }}
        py={{ xs: 1.5, md: 2 }}
        bgcolor="background.paper"
        border={1}
        borderColor="divider"
        borderRadius={2}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="subtitle2">
              {t('travelServices.booking.passengers')}
            </Typography>
            <Button
              size="small"
              startIcon={<Add />}
              disabled={passengers.length >= 9}
              onClick={() => setPassengers((prev) => [...prev, { ...EMPTY_PASSENGER }])}
            >
              {t('travelServices.passes.addPassenger')}
            </Button>
          </Grid>

          {passengers.map((pax, index) => (
            <Grid item xs={12} key={index}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={5} md={3}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={t('travelServices.booking.paxNumber', { number: index + 1 })}
                    value={pax.type}
                    onChange={(event) =>
                      setPax(index, {
                        type: event.target.value as PassPassengerPayload['type'],
                      })
                    }
                  >
                    <MenuItem value="adult">{t('travelServices.booking.adult')}</MenuItem>
                    <MenuItem value="youth">{t('travelServices.passes.youth')}</MenuItem>
                    <MenuItem value="senior">{t('travelServices.booking.senior')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label={t('travelServices.passes.age')}
                    value={pax.age}
                    inputProps={{ onWheel: releaseNumberInputOnWheel }}
                    onChange={(event) =>
                      setPax(index, {
                        age: Math.max(0, Math.min(120, Number(event.target.value) || 0)),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={3} md={4}>
                  <CountrySelect
                    fullWidth
                    required
                    label={t('travelServices.booking.nationality')}
                    value={pax.nationality}
                    onChange={(code) => setPax(index, { nationality: code ?? '' })}
                  />
                </Grid>
                {passengers.length > 1 && (
                  <Grid item xs={12} md={1}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setPassengers((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>
        {/* No hay `child`: el proveedor los cotiza como youth con su edad. */}
        <Typography variant="caption" color="text.secondary">
          {t('travelServices.passes.youthHint')}
        </Typography>
      </Box>

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describePasses(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="product" />}
      {search.isError && <Alert severity="error">{t('travelServices.searchError')}</Alert>}
      <ProviderStatusStrip providers={search.data?.providers} />
      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.passes.emptyState')}
        </Typography>
      )}

      {search.data && !search.isLoading && (
        <>
          {/* Filtros del resultado: son 300+ bundles, sin refinado no se usa. */}
          <ResultFiltersBar
            activeCount={activeFilterCount}
            resultCount={groups.length}
            onClear={() => setFilters(EMPTY_PASS_FILTERS)}
          >
            {() => (
              <>
                <TextField
                  size="small"
                  sx={{ width: 220 }}
                  label={t('travelServices.passes.filterByCountry')}
                  value={filters.query}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, query: event.target.value }))
                  }
                />
                <TextField
                  select
                  size="small"
                  sx={{ width: 150 }}
                  label={t('travelServices.passes.travelClass')}
                  value={filters.travelClass}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      travelClass: event.target.value as PassFilters['travelClass'],
                    }))
                  }
                >
                  <MenuItem value="">{t('travelServices.all')}</MenuItem>
                  <MenuItem value="2ndclass">{t('travelServices.passes.secondClass')}</MenuItem>
                  <MenuItem value="1stclass">{t('travelServices.passes.firstClass')}</MenuItem>
                </TextField>
                <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    {t('travelServices.passes.travelDays')}
                  </Typography>
                  {TRAVEL_DAY_FILTERS.map((days) => (
                    <Chip
                      key={days}
                      size="small"
                      label={`${days}+`}
                      color={filters.minTravelDays === days ? 'primary' : 'default'}
                      variant={filters.minTravelDays === days ? 'filled' : 'outlined'}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          minTravelDays: prev.minTravelDays === days ? 0 : days,
                        }))
                      }
                    />
                  ))}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={filters.globalOnly}
                      onChange={(event) =>
                        setFilters((prev) => ({ ...prev, globalOnly: event.target.checked }))
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('travelServices.passes.globalOnly')}
                    </Typography>
                  }
                />
              </>
            )}
          </ResultFiltersBar>

          <Typography variant="body2" color="text.secondary">
            {t('travelServices.passes.found', {
              count: groups.length,
              total: totalOffers,
            })}
          </Typography>

          {groups.length === 0 ? (
            <Alert severity="info">{t('travelServices.noResults')}</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {groups.map((group, index) => {
                const open = expanded === group.product;
                return (
                  <RuteroResultCard
                    key={group.product}
                    index={index}
                    media={
                      <ResultMedia icon={<Train sx={{ fontSize: 44 }} color="action" />} />
                    }
                    header={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {/* Por dónde se compra es información de OPERACIÓN:
                            en una superficie minorista no se muestra. El
                            "solo cotización" sí queda: habla del producto, no
                            del proveedor. */}
                        {isAgent && (
                          <Typography
                            component="span"
                            color="text.disabled"
                            sx={{ fontSize: 11.5 }}
                          >
                            {t('travelServices.viaProvider', {
                              provider: group.cheapest.provider,
                            })}
                          </Typography>
                        )}
                        {/* Se puede cotizar pero no reservar: se dice EN la
                            card, no en un aviso arriba del formulario que el
                            asesor ya scrolleó. */}
                        {PASSES_BOOKING_BLOCKED && (
                          <Box ml="auto">
                            <Tooltip title={t('travelServices.passes.bookingBlocked')}>
                              <Box component="span" sx={{ cursor: 'help' }}>
                                <StatusFlag
                                  tone="neutral"
                                  label={t('travelServices.passes.quoteOnly')}
                                />
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                      </Stack>
                    }
                    title={
                      <Typography sx={{ fontSize: 15.5, fontWeight: 600 }}>
                        {productTitle(group.cheapest, language)}
                      </Typography>
                    }
                    details={
                      <MetaLine
                        items={[
                          t('travelServices.passes.variants', {
                            count: group.offers.length,
                          }),
                        ]}
                      />
                    }
                    price={
                      <PriceBlock
                        pricing={group.cheapest.pricing}
                        fromLabel={t('travelServices.passes.fromPrice')}
                      />
                    }
                    action={
                      // Ver variantes NO es el CTA de la card: es abrir la
                      // franja donde están los botones que sí reservan.
                      <Button
                        variant="outlined"
                        sx={{ minWidth: 104, height: 30 }}
                        onClick={() => setExpanded(open ? null : group.product)}
                      >
                        {t(
                          open
                            ? 'travelServices.passes.hideVariants'
                            : 'travelServices.passes.showVariants',
                        )}
                      </Button>
                    }
                    fares={
                      open
                        ? group.offers.map((offer, position) => (
                          <FareRow
                            key={`${offer.provenance.slice(0, 10)}-${position}`}
                            label={t(
                              offer.travelClass === '1stclass'
                                ? 'travelServices.passes.firstClass'
                                : 'travelServices.passes.secondClass',
                            )}
                            detail={
                              isContinuous(offer)
                                ? t('travelServices.passes.continuous')
                                : t('travelServices.passes.travelDaysIn', {
                                  days: offer.travelDayCount,
                                  validity: offer.validityDays,
                                })
                            }
                            pricing={offer.pricing}
                            actionLabel={t('travelServices.book')}
                            onAction={() =>
                              setBookingQuote(
                                captureSelectableSearchQuote(
                                  offer,
                                  search.dataUpdatedAt,
                                  'passes',
                                  CONFIG.AGENCY_ID,
                                  searchPayload,
                                  Date.now(),
                                  formDiverged,
                                ),
                              )}
                            disabledReason={
                              PASSES_BOOKING_BLOCKED
                                ? t('travelServices.passes.quoteOnly')
                                : selectionBlockedReason
                            }
                          />
                        ))
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          )}
        </>
      )}

      <BookingModal
        vertical="passes"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={
          bookingOffer
            ? [
              productTitle(bookingOffer, language),
              [
                t(
                  bookingOffer.travelClass === '1stclass'
                    ? 'travelServices.passes.firstClass'
                    : 'travelServices.passes.secondClass',
                ),
                isContinuous(bookingOffer)
                  ? t('travelServices.passes.continuous')
                  : t('travelServices.passes.travelDaysIn', {
                    days: bookingOffer.travelDayCount,
                    validity: bookingOffer.validityDays,
                  }),
              ].join(' · '),
              t('travelServices.passes.ticketNotice'),
            ]
            : undefined
        }
        party={committedParty({ vertical: 'passes', payload: searchPayload })}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
